/// <amd-module name="Wiki/PageRenamePanel/PageRenamePanel" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { ICancelReason } from "VSS/Core/Util/Promise";
import { equals } from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { decodeUri } from "VSS/Platform/Util/Linking";
import { deepMerge } from "VSS/Platform/Util/Object";

import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { ContentSize } from "VSSUI/Callout";
import { Checkbox } from "VSSUI/Checkbox";
import { Link } from "VSSUI/Link";
import { IListItemDetails } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { SimpleTableCell, TableRow } from "VSSUI/Table";
import { Tab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { Tooltip } from "VSSUI/TooltipEx";

import { WikiHit, WikiResult, WikiSearchResponse } from "Wiki/Clients/Search/Contracts/Search.Shared";
import { GitVersionDescriptor, VersionControlRecursionType } from "Wiki/Clients/VC/Contracts/Git";
import { WikiPageMoveResponse, WikiPageResponse } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { WikiPage, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { WorkItemReference } from "Wiki/Clients/Wit/Contracts/WorkItemTracking";
import { BrokenLinksTab, WikiBrokenLinkItem, WitBrokenLinkItem } from "Wiki/PageRenamePanel/BrokenLinksTab";
import {
    ArtifactUriToWorkItemReferencesMap,
    IArtifactResult,
    ILinkedArtifactDisplayData,
    OperationStatus,
    WorkItemData
} from "Wiki/PageRenamePanel/IPageRenamePanelSharedInterfaces";
import {
    contains,
    fetchAllWorkItemReferencesFromArtifactUris,
    getNewPagePath,
    getPagePathWithoutRootCharacter,
    getUpdatedContent,
    getWikiPagePathFromGitPath
} from "Wiki/PageRenamePanel/PageRenamePanelHelper";
import * as Resources from "Wiki/PageRenamePanel/Resources";
import { PageRenamePanelSource } from "Wiki/PageRenamePanel/Sources/PageRenamePanelSource";
import { WikiPagesSource } from "Wiki/PageRenamePanel/Sources/WikiPagesSource";
import { getWikiPageArtifactId, getWikiPageArtifactUri, getWikiPagePathFromArtifactId } from "Wiki/PageRenamePanel/WikiPageArtifactHelper";
import { ActionIds, UrlConstants } from "Wiki/ViewCommon/Constants";
import { WikiPageInfo } from "Wiki/ViewCommon/Contracts";
import { Areas, PerformanceConstants, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { getFileName, removeMdExtensionFromPagePath } from "Wiki/ViewCommon/Utils/PathHelper";
import { getPagePathBasedUrlAndState, getVersionFromBranchName, publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IPageRenamePanelProps extends IVssComponentProperties {
    oldPagePath: string;
    newPagePath: string;

    title: string;
    description: string;

    successMessage: string;
    brokenLinksDiscoveryOrRenameFailureMessage: string;
    linksFixFailureMessage: string;

    updateButtonText: string;

    wiki: WikiV2;
    wikiVersion: GitVersionDescriptor;

    onConfirm: (closeWithoutFixing: boolean) => Promise<WikiPageResponse | WikiPageMoveResponse | undefined>;
    onDismiss: (response?: WikiPageResponse | WikiPageMoveResponse) => void;
}

export interface WikiPageUpdateData {
    pagePath: string;
    links: string[];
    updateState: OperationStatus;
}

interface FetchAllWorkItemReferencesData {
    workItemsToFixCount: number;
    workItemIdToDataMap: { [key: number]: WorkItemData };
}

interface BrokenLinksAutoFixMetrics {
    pageUpdateSuccessCount: number;
    pageUpdateFailureCount: number;
    workItemUpdateSuccessCount: number;
    workItemUpdateFailureCount: number;
}

const pageLinksTab = "page-links-tab";
const workItemsTab = "work-items-tab";
const DEFAULT_MAX_WIKI_SEARCH_RESULTS_COUNT = 200;

export class PageRenamePanel extends VssComponent<IPageRenamePanelProps> {
    private _pageRenamePanelSource: PageRenamePanelSource;
    private _wikiPagesSource: WikiPagesSource;
    private _hasPublishedTelemetry: boolean;
    private _performanceService: IVssPerformanceService;
    private _onConfirmPromise: Promise<WikiPageResponse | WikiPageMoveResponse | undefined>;
    private _brokenLinksAutoFixMetrics: BrokenLinksAutoFixMetrics;

    private _selectedTabId: ObservableValue<string>;
    private _hasEvaluationStarted: ObservableValue<boolean>;
    private _isLoadingWorkItems: ObservableValue<boolean>;
    private _isPageSearchQueryPending: ObservableValue<boolean>;
    private _isFetchingMorePageLinks: ObservableValue<boolean>;
    private _hasFetchedAllBrokenPageLinks: ObservableValue<boolean>;
    private _updateStarted: ObservableValue<boolean>;
    private _hasAutofixCompleted: ObservableValue<boolean>;
    private _autofixingLinks: ObservableValue<boolean>;

    private _shouldAutofixLinks: ObservableValue<boolean>;

    private _errorMessage: ObservableValue<string | undefined>;
    private _wikiPagesToFixCount: ObservableValue<number | undefined>;
    private _workItemsToFixCount: ObservableValue<number | undefined>;

    private _renamePageStatus: ObservableValue<OperationStatus>;

    private _wikiPagesUpdateData: ObservableValue<WikiPageUpdateData[]>;
    private _workItemIdToDataMap: ObservableValue<{ [key: number]: WorkItemData }>;

    constructor(props: IPageRenamePanelProps, context: IVssComponentContext) {
        super(props, context);
        this._pageRenamePanelSource = new PageRenamePanelSource(context.pageContext);
        this._wikiPagesSource = new WikiPagesSource(context.pageContext);
        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        this._selectedTabId = new ObservableValue(pageLinksTab);

        this._hasEvaluationStarted = new ObservableValue(false);
        this._isLoadingWorkItems = new ObservableValue(false);
        this._isPageSearchQueryPending = new ObservableValue(true);
        this._isFetchingMorePageLinks = new ObservableValue(false);
        this._hasFetchedAllBrokenPageLinks = new ObservableValue(false);
        this._updateStarted = new ObservableValue(false);
        this._hasAutofixCompleted = new ObservableValue(false);
        this._autofixingLinks = new ObservableValue(false);
        this._shouldAutofixLinks = new ObservableValue(true);

        this._errorMessage = new ObservableValue(undefined);
        this._wikiPagesToFixCount = new ObservableValue(undefined);
        this._workItemsToFixCount = new ObservableValue(undefined);
        this._renamePageStatus = new ObservableValue(OperationStatus.NotStarted);
        this._brokenLinksAutoFixMetrics = {
            pageUpdateSuccessCount: 0,
            pageUpdateFailureCount: 0,
            workItemUpdateSuccessCount: 0,
            workItemUpdateFailureCount: 0
        };
        this._workItemIdToDataMap = new ObservableValue({});
        this._wikiPagesUpdateData = new ObservableValue([]);
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._hasEvaluationStarted.value = true;
        this._isLoadingWorkItems.value = true;

        this._performanceService.startScenario(PerformanceConstants.HandleBrokenLinks, false, undefined, {
            [TelemetryConstants.NewWiki]: true
        });
        this._performanceService.addSplitTiming("EvaluationStarted", PerformanceConstants.HandleBrokenLinks);

        const promises: Promise<void>[] = [];

        promises.push(this._fetchWikiPageLinks(this.props.oldPagePath));

        promises.push(
            this.trackPromise(
                this._wikiPagesSource.getPageAndSubPages(this.props.wiki, this.props.oldPagePath, undefined, VersionControlRecursionType.Full)
            ).promise.then(
                (wikiPages: WikiPage[]) => {
                    const wikiPagePaths = wikiPages.map((wikiPage: WikiPage) => wikiPage.path);

                    return this.trackPromise(
                        _fetchAllAssociatedWorkItems(
                            this.context.pageContext,
                            this._pageRenamePanelSource,
                            wikiPagePaths,
                            this.props.wiki.projectId,
                            this.props.wiki.repositoryId
                        )
                    ).promise.then(
                        (data: FetchAllWorkItemReferencesData) => {
                            this._workItemsToFixCount.value = data.workItemsToFixCount;
                            this._isLoadingWorkItems.value = false;
                            this._workItemIdToDataMap.value = data.workItemIdToDataMap;
                        },
                        (error: ICancelReason) => {
                            if (!error.isCanceled) {
                                throw error;
                            }
                        }
                    );
                },
                (error: Error | ICancelReason) => {
                    if (!(error as ICancelReason).isCanceled) {
                        this._errorMessage.value = (error as Error).message;
                        throw error;
                    }
                }
            )
        );

        Promise.all(promises)
            .then(
                () => {
                    this._performanceService.addSplitTiming("EvaluationCompleted", PerformanceConstants.HandleBrokenLinks);
                },
                () => {
                    this._performanceService.addSplitTiming("EvaluationFailed", PerformanceConstants.HandleBrokenLinks);
                }
            )
            .then(() => {
                this._performanceService.endScenario(Areas.Wiki, PerformanceConstants.HandleBrokenLinks, false);
            });
    }

    public render(): JSX.Element {
        const { title, description, updateButtonText } = this.props;
        return (
            <CustomPanel onDismiss={this._onDismiss} size={ContentSize.Large}>
                <PanelHeader className={"pg-ren-pnl-hdr"} onDismiss={this._onDismiss} titleProps={{ text: title }} description={description} />

                <PanelContent>
                    <div className={"pg-ren-pnl-cntr flex-column scroll-hidden"}>
                        <div className="summary-section flex-column flex-noshrink">
                            <span className="body-m fontWeightSemiBold">{Resources.SummaryTitle}</span>
                            <span>{Resources.SummaryDescription}</span>
                        </div>

                        <Observer
                            wikiPagesToFixCount={this._wikiPagesToFixCount}
                            workItemsToFixCount={this._workItemsToFixCount}
                            selectedTabId={this._selectedTabId}
                        >
                            {(props: { wikiPagesToFixCount: number | undefined; workItemsToFixCount: number | undefined; selectedTabId: string }) => {
                                return (
                                    <TabBar
                                        onSelectedTabChanged={this._onSelectedTabChanged}
                                        selectedTabId={props.selectedTabId}
                                        tabSize={TabSize.Tall}
                                    >
                                        <Tab
                                            name={Resources.PageLinksTabName}
                                            id={pageLinksTab}
                                            key={pageLinksTab}
                                            badgeCount={props.wikiPagesToFixCount}
                                        />
                                        <Tab
                                            name={Resources.WorkItemsTabName}
                                            id={workItemsTab}
                                            key={workItemsTab}
                                            badgeCount={props.workItemsToFixCount}
                                        />
                                    </TabBar>
                                );
                            }}
                        </Observer>
                        <TabContent>
                            <Observer
                                selectedTabId={this._selectedTabId}
                                isPageSearchQueryPending={this._isPageSearchQueryPending}
                                hasFetchedAllBrokenPageLinks={this._hasFetchedAllBrokenPageLinks}
                                autofixingLinks={this._autofixingLinks}
                                isLoadingWorkItems={this._isLoadingWorkItems}
                                wikiPagesUpdateData={this._wikiPagesUpdateData}
                                workItemIdToDataMap={this._workItemIdToDataMap}
                                renamePageStatus={this._renamePageStatus}
                            >
                                {(props: {
                                    selectedTabId: string;
                                    isPageSearchQueryPending: boolean;
                                    isFetchingMorePageLinks?: boolean; // TODO: this is not observed
                                    hasFetchedAllBrokenPageLinks: boolean;
                                    autofixingLinks: boolean;
                                    isLoadingWorkItems: boolean;
                                    wikiPagesUpdateData: WikiPageUpdateData[];
                                    workItemIdToDataMap: { [key: number]: WorkItemData };
                                    renamePageStatus: OperationStatus;
                                }) => {
                                    const showStatusColumn = props.autofixingLinks && props.renamePageStatus === OperationStatus.Completed;

                                    return (
                                        <>
                                            {props.selectedTabId === pageLinksTab && (
                                                <BrokenLinksTab
                                                    items={this._getBrokenPageLinks(
                                                        props.wikiPagesUpdateData,
                                                        props.isFetchingMorePageLinks!,
                                                        props.hasFetchedAllBrokenPageLinks
                                                    )}
                                                    isLoading={props.isPageSearchQueryPending}
                                                    loadingSpinnerText={Resources.LoadingWikiPageLinksText}
                                                    showStatusColumn={showStatusColumn}
                                                    artifactColumnHeaderName={Resources.BrokenLinkComponentTable_ColumnHeader_WikiPage}
                                                    getArtifactCellContent={this._getWikiPageArtifactCellContent}
                                                />
                                            )}
                                            {props.selectedTabId === workItemsTab && (
                                                <BrokenLinksTab
                                                    items={_getBrokenWorkItems(props.workItemIdToDataMap)}
                                                    isLoading={props.isLoadingWorkItems}
                                                    loadingSpinnerText={Resources.LoadingWorkItemsText}
                                                    showStatusColumn={showStatusColumn}
                                                    artifactColumnHeaderName={Resources.BrokenLinkComponentTable_ColumnHeader_WorkItem}
                                                    getArtifactCellContent={this._getWitPageArtifactCellContent}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </Observer>
                        </TabContent>
                    </div>
                </PanelContent>
                <PanelFooter className={"pg-ren-pnl-footer flex-column flex-noshrink"}>
                    <Observer
                        isLoadingWorkItems={this._isLoadingWorkItems}
                        isPageSearchQueryPending={this._isPageSearchQueryPending}
                        isFetchingMorePageLinks={this._isFetchingMorePageLinks}
                        updateStarted={this._updateStarted}
                        shouldAutofixLinks={this._shouldAutofixLinks}
                        hasAutofixCompleted={this._hasAutofixCompleted}
                        errorMessage={this._errorMessage}
                        renamePageStatus={this._renamePageStatus}
                    >
                        {(props: {
                            isLoadingWorkItems: boolean;
                            isPageSearchQueryPending: boolean;
                            isFetchingMorePageLinks: boolean;
                            updateStarted: boolean;
                            shouldAutofixLinks: boolean;
                            hasAutofixCompleted: boolean;
                            errorMessage: string | undefined;
                            renamePageStatus: OperationStatus;
                        }) => {
                            const isRenameAndFixLinksComplete =
                                props.hasAutofixCompleted ||
                                props.renamePageStatus === OperationStatus.Failed ||
                                props.errorMessage ||
                                (!props.shouldAutofixLinks && props.renamePageStatus === OperationStatus.Completed);

                            const isRenameOrAutoFixInProgress =
                                props.renamePageStatus === OperationStatus.InProgress ||
                                (props.shouldAutofixLinks && this._isAutofixInProgress(props.updateStarted, props.hasAutofixCompleted));

                            const hasBrokenLinksDiscoveryFailed = !!props.errorMessage;
                            const hasRenameFailed = props.renamePageStatus === OperationStatus.Failed;
                            const hasAutoFixFailed = props.shouldAutofixLinks && this._hasAutoFixFailures();

                            const showError = hasBrokenLinksDiscoveryFailed || hasRenameFailed || hasAutoFixFailed;

                            return (
                                <>
                                    <div className="footer-line-one">
                                        {!isRenameAndFixLinksComplete ? (
                                            <Checkbox
                                                label={Resources.AutoFix_Checkbox_Label}
                                                checked={props.shouldAutofixLinks}
                                                onChange={this._onAutofixFlagChange}
                                                disabled={
                                                    props.isLoadingWorkItems ||
                                                    props.isPageSearchQueryPending ||
                                                    isRenameOrAutoFixInProgress ||
                                                    this._hasNoBrokenLink()
                                                }
                                            />
                                        ) : (
                                            <div className="flex-row flex-center status-summary-ctnr">
                                                <Status {...(showError ? Statuses.Failed : Statuses.Success)} size={StatusSize.m} />
                                                <span
                                                    className={"ren-operation-status-summary secondary-text fontSizeM"}
                                                    role={showError ? "alert" : ""}
                                                >
                                                    {showError
                                                        ? hasBrokenLinksDiscoveryFailed || hasRenameFailed
                                                            ? this.props.brokenLinksDiscoveryOrRenameFailureMessage
                                                            : this.props.linksFixFailureMessage
                                                        : this.props.successMessage}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <ButtonGroup className="bolt-panel-footer-buttons flex-grow">
                                        <Button
                                            key={"cancel-btn"}
                                            text={isRenameAndFixLinksComplete ? Resources.Close : Resources.Cancel}
                                            onClick={this._cancelButtonClick}
                                            disabled={isRenameOrAutoFixInProgress}
                                        />
                                        {!isRenameAndFixLinksComplete && (
                                            <Button
                                                key={"update-btn"}
                                                primary={true}
                                                onClick={this._onUpdateButtonClick}
                                                disabled={
                                                    props.isLoadingWorkItems ||
                                                    props.isPageSearchQueryPending ||
                                                    props.isFetchingMorePageLinks ||
                                                    props.updateStarted ||
                                                    props.renamePageStatus === OperationStatus.InProgress
                                                }
                                            >
                                                {isRenameOrAutoFixInProgress && <Spinner className="icon-margin" size={SpinnerSize.small} />}
                                                {updateButtonText}
                                            </Button>
                                        )}
                                    </ButtonGroup>
                                </>
                            );
                        }}
                    </Observer>
                </PanelFooter>
            </CustomPanel>
        );
    }

    private _getWikiPageArtifactCellContent = (tableItem: WikiBrokenLinkItem): JSX.Element => {
        const editPageUrl = getPagePathBasedUrlAndState(
            this.context.pageContext,
            {
                pagePath: tableItem.pagePath,
                [UrlConstants.ActionParam]: ActionIds.Edit
            },
            { wikiIdentifier: this.props.wiki.name, wikiVersion: getVersionFromBranchName(this.props.wikiVersion.version) }
        ).url;
        return (
            <Tooltip text={tableItem.pagePath} overflowOnly>
                <Link className={"bolt-table-link text-ellipsis"} href={editPageUrl} target={"_blank"} rel={"noreferrer noopener nofollow"} excludeTabStop>
                    {getFileName(tableItem.pagePath)}
                </Link>
            </Tooltip>
        );
    };

    private _getWitPageArtifactCellContent = (tableItem: WitBrokenLinkItem): JSX.Element => {
        return (
            <LegacyComponent
                modules={["Presentation/Scripts/TFS/Controls/LinkedArtifacts/Control", "Wiki/Scripts/PrimaryArtifactComponentNwpWrapper"]}
                wrappedType="Wiki.PrimaryArtifactRenderer.Component"
                wrappedProps={{
                    primaryData: tableItem.workItem.primaryData
                }}
            />
        );
    };

    private _getBrokenPageLinks(
        wikiPagesUpdateData: WikiPageUpdateData[],
        isFetchingMorePageLinks: boolean,
        hasFetchedAllBrokenPageLinks: boolean
    ): WikiBrokenLinkItem[] {
        const items: WikiBrokenLinkItem[] = [];

        if (wikiPagesUpdateData) {
            wikiPagesUpdateData.forEach((wikiPageUpdateData: WikiPageUpdateData) => {
                wikiPageUpdateData.links.forEach((link: string) => {
                    items.push({
                        pagePath: wikiPageUpdateData.pagePath,
                        link: link,
                        updateState: wikiPageUpdateData.updateState
                    });
                });
            });

            if (isFetchingMorePageLinks) {
                // For showing shimmer in the table while row is loading
                items.push((undefined as any) as WikiBrokenLinkItem);
            } else if (!hasFetchedAllBrokenPageLinks) {
                // For showing "show more" link row
                items.push(({
                    renderRow: this._renderShowMoreRow
                } as any) as WikiBrokenLinkItem);
            }
        }

        return items;
    }

    private _renderShowMoreRow = (rowIndex: number, item: WikiBrokenLinkItem, details: IListItemDetails<WikiBrokenLinkItem>): JSX.Element => {
        const rowDetails = { ...details, itemProvider: this.props.itemProvider };
        return (
            <TableRow details={rowDetails} index={rowIndex} linkProps={(item as any).linkProps}>
                {SimpleTableCell({
                    columnIndex: 0,
                    children: (
                        <Link className="brkn-links-show-more-link" onClick={this._onShowMoreLinkClick} key="show-more">
                            {Resources.ShowMoreText}
                        </Link>
                    )
                })}
            </TableRow>
        );
    };

    private _onShowMoreLinkClick = (): void => {
        this._loadMoreWikiPageLinks(this.props.oldPagePath);
    };

    private _onSelectedTabChanged = (newTabId: string): void => {
        this._selectedTabId.value = newTabId;
    };

    private _cancelButtonClick = (): void => {
        if (!this._onConfirmPromise) {
            this._onDismiss();
        } else {
            this._onConfirmPromise.then(
                response => {
                    return this._onDismiss(response);
                },
                () => {
                    this._onDismiss();
                }
            );
        }
    };

    private _onDismiss = (response?: WikiPageResponse | WikiPageMoveResponse): void => {
        this._publishTelemetry(this._autofixingLinks.value);
        this.props.onDismiss(response);
    };

    private _onAutofixFlagChange = (event: React.SyntheticEvent<HTMLElement>, checked: boolean): void => {
        this._shouldAutofixLinks.value = checked;
    };

    private _onUpdateButtonClick = (): void => {
        this._renamePageStatus.value = OperationStatus.InProgress;
        this._onUpdateStarted(this._shouldAutofixLinks.value);
        this._onConfirmPromise = this.trackPromise(this.props.onConfirm(!this._shouldAutofixLinks.value)).promise;

        this._onConfirmPromise.then(
            response => {
                this._renamePageStatus.value = OperationStatus.Completed;

                if (this._shouldAutofixLinks.value && !this._hasNoBrokenLink()) {
                    this._handleBrokenLinks(this.props.oldPagePath, this.props.newPagePath).then(() => {
                        if (
                            this._brokenLinksAutoFixMetrics.pageUpdateFailureCount === 0 &&
                            this._brokenLinksAutoFixMetrics.workItemUpdateFailureCount === 0
                        ) {
                            this._onDismiss(response);
                        }
                    });
                } else {
                    this._onDismiss(response);
                }
            },
            (error: ICancelReason) => {
                if (!error.isCanceled) {
                    this._renamePageStatus.value = OperationStatus.Failed;
                }
            }
        );
    };

    private _onUpdateStarted(autofixingLinks?: boolean): void {
        if (!autofixingLinks) {
            // publish telemetry now as the dialog will close when the update is complete
            this._publishTelemetry(false);
        }
        this._updateStarted.value = true;
        this._autofixingLinks.value = !!autofixingLinks;
    }

    private _loadMoreWikiPageLinks(pagePath: string): Promise<void> {
        if (!this._hasFetchedAllBrokenPageLinks.value && !this._isFetchingMorePageLinks.value) {
            return this._fetchWikiPageLinks(pagePath, true, this._wikiPagesUpdateData.value.length);
        } else {
            return Promise.resolve();
        }
    }

    private _fetchWikiPageLinks(
        pagePath: string,
        isLoadMore: boolean = false,
        skipCount: number = 0,
        maxResultsCount: number = DEFAULT_MAX_WIKI_SEARCH_RESULTS_COUNT
    ): Promise<void> {
        if (isLoadMore) {
            this._isFetchingMorePageLinks.value = isLoadMore;
        }

        return this.trackPromise(
            this._pageRenamePanelSource.queryWikiPageNameSearch(pagePath, skipCount, maxResultsCount, this.props.wiki.projectId)
        ).promise.then(
            (wikiSearchResponse: WikiSearchResponse) => {
                const wikiPagesToFixCount = wikiSearchResponse.count;
                let wikiPagesUpdateData = _handleWikiPageSearchResultLoad(wikiSearchResponse);

                if (isLoadMore) {
                    wikiPagesUpdateData = this._wikiPagesUpdateData.value.concat(wikiPagesUpdateData);
                    this._isFetchingMorePageLinks.value = false;
                }

                this._wikiPagesToFixCount.value = wikiPagesToFixCount;
                this._isPageSearchQueryPending.value = false;
                this._hasFetchedAllBrokenPageLinks.value = wikiPagesUpdateData.length === wikiSearchResponse.count;

                this._wikiPagesUpdateData.value = wikiPagesUpdateData;
            },
            (error: ICancelReason) => {
                if (!error.isCanceled) {
                    throw error;
                }
            }
        );
    }

    private _handleBrokenLinks(topLevelPageOldPath: string, topLevelPageNewPath: string): Promise<void> {
        const promises: Promise<void>[] = [];
        this._performanceService.startScenario(PerformanceConstants.BrokenLinksAutoFix, false, undefined, {
            [TelemetryConstants.NewWiki]: true
        });
        promises.push(
            this.trackPromise(this._getAllBrokenWikiPageLinks(topLevelPageOldPath)).promise.then(() => {
                return this._fixWikiPageLinks(this._wikiPagesUpdateData.value, topLevelPageOldPath, topLevelPageNewPath);
            })
        );

        promises.push(this._fixWorkItemUris(this._workItemIdToDataMap.value, topLevelPageOldPath, topLevelPageNewPath));

        return Promise.all(promises).then(() => {
            this._performanceService.endScenario(Areas.Wiki, PerformanceConstants.BrokenLinksAutoFix, false);
            this._publishTelemetry(true);
            announce(this._hasAutoFixFailures() ? Resources.UpdateLinksFailedWarningMessage : this.props.successMessage, true);
            this._hasAutofixCompleted.value = true;

            return;
        });
    }

    private _getAllBrokenWikiPageLinks(pagePath: string): Promise<void> {
        if (!this._hasFetchedAllBrokenPageLinks.value) {
            return this._loadMoreWikiPageLinks(pagePath).then(() => {
                return this._getAllBrokenWikiPageLinks(pagePath);
            });
        } else {
            return Promise.resolve();
        }
    }

    private _fixWikiPageLinks(wikiPagesUpdateData: WikiPageUpdateData[], topLevelPageOldPath: string, topLevelPageNewPath: string): Promise<void> {
        if (!wikiPagesUpdateData || wikiPagesUpdateData.length === 0) {
            return Promise.resolve();
        }

        // Initial promise to start the chaining
        let promise = Promise.resolve();

        for (const wikiPageUpdateData of wikiPagesUpdateData) {
            const oldPagePath = wikiPageUpdateData.pagePath;
            const newPagePath = getNewPagePath(oldPagePath, topLevelPageOldPath, topLevelPageNewPath);

            promise = promise.then(() => {
                return _updateWikiPageWithRetrial(
                    this.context.pageContext,
                    this._wikiPagesSource,
                    this.props.wiki,
                    this.props.wikiVersion,
                    oldPagePath,
                    newPagePath,
                    topLevelPageOldPath,
                    topLevelPageNewPath
                ).then(
                    () => {
                        this._onPageLinksUpdated(oldPagePath);
                    },
                    () => {
                        this._onPageLinksUpdateFailed(oldPagePath);
                    }
                );
            });
        }

        return promise;
    }

    private _fixWorkItemUris(
        workItemIdToDataMap: { [key: number]: WorkItemData },
        topLevelPageOldPagePath: string,
        topLevelPageNewPagePath: string
    ): Promise<void> {
        const workItemIdToUpdateUris: { [key: number]: { [key: string]: string } } = {};
        let shouldUpdateWorkItems = false;
        const { projectId, repositoryId } = this.props.wiki;

        for (const workItemId in workItemIdToDataMap) {
            if (workItemIdToDataMap.hasOwnProperty(workItemId)) {
                const wikiPagePaths = workItemIdToDataMap[workItemId].wikiPagePaths;
                const updateUris: { [key: string]: string } = {};

                wikiPagePaths.forEach((pagePath: string) => {
                    const newPagePath = getNewPagePath(pagePath, topLevelPageOldPagePath, topLevelPageNewPagePath);
                    const newUri = getWikiPageArtifactUri(getWikiPageArtifactId(projectId, repositoryId, newPagePath));
                    const oldUri = getWikiPageArtifactUri(getWikiPageArtifactId(projectId, repositoryId, pagePath));

                    updateUris[oldUri] = newUri;
                });
                workItemIdToUpdateUris[parseInt(workItemId)] = updateUris;
                shouldUpdateWorkItems = true;
            }
        }

        if (shouldUpdateWorkItems) {
            return this.trackPromise(this._pageRenamePanelSource.updateWorkItemsArtifactUris(workItemIdToUpdateUris)).promise.then(
                (updateResults: { [key: number]: boolean }) => {
                    this._onWorkItemUpdateResultsLoaded(updateResults);
                },
                (error: ICancelReason) => {
                    if (!error.isCanceled) {
                        this._onWorkItemsUpdatesFailed();
                    }
                }
            );
        } else {
            return Promise.resolve();
        }
    }

    private _onWorkItemUpdateResultsLoaded(updateResults: { [key: number]: boolean }): void {
        const workItemIdToDataMap = { ...this._workItemIdToDataMap.value };

        if (updateResults) {
            for (const workItemId in workItemIdToDataMap) {
                if (workItemIdToDataMap.hasOwnProperty(workItemId)) {
                    const success = updateResults[workItemId];

                    if (success) {
                        this._brokenLinksAutoFixMetrics.workItemUpdateSuccessCount++;
                        workItemIdToDataMap[workItemId].updateState = OperationStatus.Completed;
                    } else {
                        this._brokenLinksAutoFixMetrics.workItemUpdateFailureCount++;
                        workItemIdToDataMap[workItemId].updateState = OperationStatus.Failed;
                    }
                }
            }
            this._workItemIdToDataMap.value = workItemIdToDataMap;
        }
    }

    private _onWorkItemsUpdatesFailed(): void {
        const workItemIdToDataMap = { ...this._workItemIdToDataMap.value };

        for (const workItemId in workItemIdToDataMap) {
            if (workItemIdToDataMap.hasOwnProperty(workItemId)) {
                workItemIdToDataMap[workItemId].updateState = OperationStatus.Failed;
                this._brokenLinksAutoFixMetrics.workItemUpdateFailureCount++;
            }
        }
        this._workItemIdToDataMap.value = workItemIdToDataMap;
    }

    private _onPageLinksUpdated(pagePath: string): void {
        const wikiPagesUpdateData = deepMerge([], this._wikiPagesUpdateData.value);

        const wikiPageUpdateData = wikiPagesUpdateData.find((pageUpdateData: WikiPageUpdateData) => pageUpdateData.pagePath === pagePath);
        if (wikiPageUpdateData) {
            this._brokenLinksAutoFixMetrics.pageUpdateSuccessCount++;
            wikiPageUpdateData.updateState = OperationStatus.Completed;
        }
        this._wikiPagesUpdateData.value = wikiPagesUpdateData;
    }

    private _onPageLinksUpdateFailed(pagePath: string): void {
        const wikiPagesUpdateData = deepMerge([], this._wikiPagesUpdateData.value);

        const wikiPageUpdateData = wikiPagesUpdateData.find((pageUpdateData: WikiPageUpdateData) => pageUpdateData.pagePath === pagePath);
        if (wikiPageUpdateData) {
            this._brokenLinksAutoFixMetrics.pageUpdateFailureCount++;
            wikiPageUpdateData.updateState = OperationStatus.Failed;
        }

        this._wikiPagesUpdateData.value = wikiPagesUpdateData;
    }

    private _hasAutoFixFailures(): boolean {
        return this._brokenLinksAutoFixMetrics.pageUpdateFailureCount + this._brokenLinksAutoFixMetrics.workItemUpdateFailureCount > 0;
    }

    private _isAutofixInProgress(updateStarted: boolean, hasAutofixCompleted: boolean): boolean {
        return updateStarted && this._renamePageStatus.value !== OperationStatus.Failed && !hasAutofixCompleted;
    }

    private _hasNoBrokenLink = (): boolean => {
        return (
            this._hasEvaluationStarted.value &&
            !this._isLoadingWorkItems.value &&
            !this._isPageSearchQueryPending.value &&
            this._wikiPagesToFixCount.value === 0 &&
            this._workItemsToFixCount.value === 0
        );
    };

    /// <summary>
    /// Called to trace CI log for aggregated set of properties.
    /// Multiple calls to this method will log telemetry only once per instance of the dialog.
    /// If user aborts the move/rename operation at any stage, "isAutofixing"
    /// both should be set to false.
    /// </summary>
    /// <param name="isAutofixing">It should true only if user clicked on autofix option.</param>
    private _publishTelemetry(isAutofixing: boolean): void {
        if (!this._hasPublishedTelemetry) {
            const telemetryData = {
                ...this._brokenLinksAutoFixMetrics,
                evaluationStarted: this._hasEvaluationStarted.value,
                pagesToFixCount: this._wikiPagesToFixCount.value,
                workItemsToFixCount: this._workItemsToFixCount.value,
                autofixing: isAutofixing
            };
            publish(this.context.pageContext, TelemetryConstants.HandleBrokenLinks, this.props.wiki.type, telemetryData);
            this._hasPublishedTelemetry = true;
        }
    }
}

function _fetchAllAssociatedWorkItems(
    pageContext: IVssPageContext,
    pageRenamePanelSource: PageRenamePanelSource,
    wikiPagePaths: string[],
    projectId: string,
    repositoryId: string
): Promise<FetchAllWorkItemReferencesData> {
    const wikiPageArtifactUris = wikiPagePaths.map((pagePath: string) =>
        getWikiPageArtifactUri(getWikiPageArtifactId(projectId, repositoryId, pagePath))
    );

    return fetchAllWorkItemReferencesFromArtifactUris(pageContext, wikiPageArtifactUris).then(
        (artifactUrisWorkItemReferencesMap: ArtifactUriToWorkItemReferencesMap) => {
            const workItemIds: number[] = [];
            const workItemIdMap: { [key: number]: boolean } = {};

            wikiPageArtifactUris.forEach((artifactUri: string) => {
                artifactUrisWorkItemReferencesMap[artifactUri].forEach((workItemReference: WorkItemReference) => {
                    const workItemId = workItemReference.id;

                    if (!workItemIdMap[workItemId]) {
                        workItemIdMap[workItemId] = true;
                        workItemIds.push(workItemId);
                    }
                });
            });

            return pageRenamePanelSource.resolveArtifacts(workItemIds).then((artifactResult: IArtifactResult) => {
                return _onWorkItemsDisplayDataLoaded(artifactUrisWorkItemReferencesMap, artifactResult.resolvedArtifacts);
            });
        }
    );
}

function _onWorkItemsDisplayDataLoaded(
    artifactUrisQueryResult: ArtifactUriToWorkItemReferencesMap,
    workItemsDisplayData: ILinkedArtifactDisplayData[]
): FetchAllWorkItemReferencesData {
    const idToWorkItemDisplayDataMap: { [key: number]: ILinkedArtifactDisplayData } = {};

    let workItemsToFixCount = 0;
    workItemsDisplayData.forEach((workItemDisplayData: ILinkedArtifactDisplayData) => {
        workItemsToFixCount++;
        idToWorkItemDisplayDataMap[(workItemDisplayData.id as unknown) as number] = workItemDisplayData;
    });

    const workItemIdToDataMap: { [key: number]: WorkItemData } = {};

    if (artifactUrisQueryResult) {
        for (const wikiPageUri in artifactUrisQueryResult) {
            if (artifactUrisQueryResult.hasOwnProperty(wikiPageUri)) {
                const wikiPagePath = getWikiPagePathFromArtifactId(decodeUri(wikiPageUri).id);

                artifactUrisQueryResult[wikiPageUri].forEach((workItemReference: WorkItemReference) => {
                    const workItemId = workItemReference.id;
                    if (workItemIdToDataMap[workItemId]) {
                        workItemIdToDataMap[workItemId].wikiPagePaths.push(wikiPagePath);
                    } else {
                        workItemIdToDataMap[workItemId] = {
                            displayData: idToWorkItemDisplayDataMap[workItemId],
                            updateState: OperationStatus.InProgress,
                            wikiPagePaths: [wikiPagePath]
                        };
                    }
                });
            }
        }
    }

    return {
        workItemsToFixCount,
        workItemIdToDataMap
    };
}

function _handleWikiPageSearchResultLoad(wikiSearchResponse: WikiSearchResponse): WikiPageUpdateData[] {
    const wikiPagesUpdateData: WikiPageUpdateData[] = [];
    if (wikiSearchResponse && wikiSearchResponse.results) {
        const xmlParser = new DOMParser();
        wikiSearchResponse.results.forEach((wikiResult: WikiResult) => {
            const links: string[] = [];
            wikiResult.hits.forEach((wikiHit: WikiHit) => {
                if (equals(wikiHit.fieldReferenceName, "contentLinks.lower", true)) {
                    wikiHit.highlights.forEach((highlight: string) => {
                        try {
                            const parsedXml = xmlParser.parseFromString(highlight, "text/xml");
                            const link = parsedXml.getElementsByTagName("highlighthit")[0].childNodes[0].nodeValue;

                            if (link && !contains(links, link)) {
                                links.push(link);
                            }
                        } catch (e) {
                            // xmlParser.parseFromString can throw exception if highlight content is malformed.
                        }
                    });
                }
            });
            const pagePath = getWikiPagePathFromGitPath(removeMdExtensionFromPagePath(wikiResult.path));
            if (pagePath) {
                wikiPagesUpdateData.push({
                    pagePath,
                    links,
                    updateState: OperationStatus.InProgress
                });
            }
        });
    }

    return wikiPagesUpdateData;
}

function _getBrokenWorkItems(workItemIdToDataMap: { [key: number]: WorkItemData }): WitBrokenLinkItem[] {
    const items: WitBrokenLinkItem[] = [];
    if (workItemIdToDataMap) {
        for (const workItemId in workItemIdToDataMap) {
            if (workItemIdToDataMap.hasOwnProperty(workItemId)) {
                const workItemData = workItemIdToDataMap[workItemId];
                workItemData.wikiPagePaths.forEach((wikiPagePath: string) => {
                    items.push({
                        workItem: workItemData.displayData,
                        link: getPagePathWithoutRootCharacter(wikiPagePath),
                        updateState: workItemData.updateState
                    });
                });
            }
        }
    }

    return items;
}

function _updateWikiPageWithRetrial(
    pageContext: IVssPageContext,
    wikiPagesSource: WikiPagesSource,
    wiki: WikiV2,
    wikiVersion: GitVersionDescriptor,
    oldPagePath: string,
    newPagePath: string,
    topLevelPageOldPath: string,
    topLevelPageNewPath: string,
    totalAttempts: number = 0
): Promise<void> {
    const maxUpdateAttempts = 3;

    return wikiPagesSource
        .getPageInfo(wiki, newPagePath, wikiVersion)
        .then((pageInfo: WikiPageInfo) => {
            return wikiPagesSource.savePage(
                wiki,
                newPagePath,
                getUpdatedContent(pageContext, pageInfo.content, topLevelPageOldPath, topLevelPageNewPath),
                pageInfo.version!,
                Resources.RenamePageDialogCommitMessage
            );
        })
        .then(
            () => {
                // Do nothing. Promise will be fulfilled.
            },
            (error: Error) => {
                if (++totalAttempts < maxUpdateAttempts) {
                    return _updateWikiPageWithRetrial(
                        pageContext,
                        wikiPagesSource,
                        wiki,
                        wikiVersion,
                        oldPagePath,
                        newPagePath,
                        topLevelPageOldPath,
                        topLevelPageNewPath,
                        totalAttempts
                    );
                } else {
                    throw error;
                }
            }
        );
}

VssComponent.register("Wiki.PageRenamePanel", PageRenamePanel);
